import React, { useMemo, useState } from 'react';
import { useSession } from '@supabase/auth-helpers-react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

type RequirementType = 'custom' | 'best_of_breed';

const levelOptions = [
  'Analyst',
  'Associate',
  'Senior Associate',
  'Vice President',
  'Director',
  'Managing Director',
  'C-Suite',
  'Other',
];

const getInitials = (name: string): string => {
  const trimmed = name.trim();
  if (!trimmed) return 'EM';
  return trimmed
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || '')
    .join('');
};

type ValidationErrors = {
  roleTitle?: string;
  level?: string;
  industry?: string;
  requirementType?: string;
};

const RequestSimulation: React.FC = () => {
  const session = useSession();
  const navigate = useNavigate();
  const [roleTitle, setRoleTitle] = useState('');
  const [level, setLevel] = useState('');
  const [industry, setIndustry] = useState('');
  const [requirementType, setRequirementType] = useState<RequirementType | null>(null);
  const [requirements, setRequirements] = useState('');
  const [notes, setNotes] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});

  const companyName =
    (session?.user?.app_metadata?.companyName as string | undefined) ||
    (session?.user?.app_metadata?.company as string | undefined) ||
    'Employer Account';
  const userName =
    (session?.user?.app_metadata?.preferredName as string | undefined) ||
    session?.user?.email?.split('@')[0] ||
    'Employer';
  const avatarInitials = useMemo(() => getInitials(userName), [userName]);

  const validateForm = (): ValidationErrors => {
    const nextErrors: ValidationErrors = {};

    if (!roleTitle.trim()) nextErrors.roleTitle = 'Role Title is required.';
    if (!level.trim()) nextErrors.level = 'Level / Seniority is required.';
    if (!industry.trim()) nextErrors.industry = 'Industry / Sector is required.';
    if (!requirementType) nextErrors.requirementType = 'Please select a skill requirements option.';

    return nextErrors;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitError(null);
    const nextErrors = validateForm();
    setValidationErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    setSubmitting(true);

    try {
      await api.post('/sim/request-simulation', {
        role_title: roleTitle.trim(),
        level: level.trim(),
        industry: industry.trim() || null,
        requirement_type: requirementType,
        requirements: requirementType === 'custom' ? requirements.trim() : null,
        notes: notes.trim() || null,
      });
      setSubmitted(true);
    } catch (error: any) {
      const message =
        error?.response?.data?.error?.message || 'Submission failed. Please retry.';
      setSubmitError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8faf9] font-body text-[#0f172a]">
      <header className="w-full bg-[#0d9488] px-8 h-14 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-[10px] bg-white/20 text-[15px] font-bold text-white">I</div>
          <p className="font-display text-[16px] font-bold text-white">Intellerview</p>
        </div>
        <div className="flex items-center gap-3">
          <p className="text-[13px] text-white/75">{companyName}</p>
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20 text-[11px] font-bold text-white">{avatarInitials}</div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-[980px] px-6 py-8">
        <div className="mx-auto max-w-[980px] rounded-[14px] border border-[#e2e5eb] bg-white p-6 sm:p-7">
          {!submitted ? (
            <>
              <div>
                <h1 className="font-display text-[24px] font-bold text-[#0f172a]">Request a New Role Simulation</h1>
                <p className="mt-2 text-[14px] text-[#64748b]">
                  We&apos;ll build a role-specific simulation tailored to your requirements.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="mt-5 space-y-4">
                {Object.keys(validationErrors).length > 0 ? (
                  <div className="rounded-[10px] border border-[#fecaca] bg-[#fef2f2] px-4 py-3 text-[13px] text-[#b91c1c]">
                    Please complete the required fields below.
                  </div>
                ) : null}
                <div className="space-y-1.5">
                  <label className="block text-[12px] font-semibold text-[#475569]">Role Title *</label>
                  <input
                    type="text"
                    value={roleTitle}
                    onChange={(event) => {
                      setRoleTitle(event.target.value);
                      if (validationErrors.roleTitle) {
                        setValidationErrors((current) => ({ ...current, roleTitle: undefined }));
                      }
                    }}
                    required
                    placeholder="e.g., Investment Banking VP"
                    className="w-full rounded-[10px] border border-[#e2e5eb] bg-white px-4 py-2.5 text-[14px] text-[#0f172a] placeholder:text-[#94a3b8] focus:border-[#0d9488] focus:outline-none focus:ring-2 focus:ring-[#0d9488]/20"
                  />
                  {validationErrors.roleTitle ? (
                    <p className="text-[12px] text-[#b91c1c]">{validationErrors.roleTitle}</p>
                  ) : null}
                </div>

                <div className="space-y-1.5">
                  <label className="block text-[12px] font-semibold text-[#475569]">Level / Seniority *</label>
                  <select
                    value={level}
                    onChange={(event) => {
                      setLevel(event.target.value);
                      if (validationErrors.level) {
                        setValidationErrors((current) => ({ ...current, level: undefined }));
                      }
                    }}
                    required
                    className="w-full rounded-[10px] border border-[#e2e5eb] bg-white px-4 py-2.5 text-[14px] text-[#0f172a] focus:border-[#0d9488] focus:outline-none focus:ring-2 focus:ring-[#0d9488]/20"
                  >
                    <option value="" disabled>
                      Select level...
                    </option>
                    {levelOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                  {validationErrors.level ? (
                    <p className="text-[12px] text-[#b91c1c]">{validationErrors.level}</p>
                  ) : null}
                </div>

                <div className="space-y-1.5">
                  <label className="block text-[12px] font-semibold text-[#475569]">Industry / Sector *</label>
                  <input
                    type="text"
                    value={industry}
                    onChange={(event) => {
                      setIndustry(event.target.value);
                      if (validationErrors.industry) {
                        setValidationErrors((current) => ({ ...current, industry: undefined }));
                      }
                    }}
                    placeholder="e.g., Mining & Metals, Technology, Healthcare"
                    className="w-full rounded-[10px] border border-[#e2e5eb] bg-white px-4 py-2.5 text-[14px] text-[#0f172a] placeholder:text-[#94a3b8] focus:border-[#0d9488] focus:outline-none focus:ring-2 focus:ring-[#0d9488]/20"
                  />
                  {validationErrors.industry ? (
                    <p className="text-[12px] text-[#b91c1c]">{validationErrors.industry}</p>
                  ) : null}
                </div>

                <div className="space-y-3">
                  <label className="block text-[12px] font-semibold text-[#475569]">Skill Requirements *</label>
                  <div className="grid gap-3">
                    <div className="space-y-3">
                      <button
                        type="button"
                        onClick={() => {
                          setRequirementType('custom');
                          if (validationErrors.requirementType) {
                            setValidationErrors((current) => ({ ...current, requirementType: undefined }));
                          }
                        }}
                        className={`w-full rounded-[14px] border p-4 text-left transition-colors ${
                          requirementType === 'custom'
                            ? 'border-[#0d9488] bg-[#f0fdfa]'
                            : 'border-[#e2e5eb] bg-white hover:border-[#0d9488]'
                        }`}
                      >
                        <p className="text-[14px] font-semibold text-[#0f172a]">I&apos;ll specify the requirements</p>
                      </button>
                      {requirementType === 'custom' ? (
                        <div className="space-y-1.5">
                          <label className="block text-[12px] font-semibold text-[#475569]">Hard skill requirements</label>
                          <textarea
                            value={requirements}
                            onChange={(event) => setRequirements(event.target.value)}
                            required={requirementType === 'custom'}
                            rows={6}
                            placeholder="Describe the technical skills, competencies, and knowledge areas you want tested..."
                            className="w-full rounded-[10px] border border-[#e2e5eb] bg-white px-4 py-2.5 text-[14px] text-[#0f172a] placeholder:text-[#94a3b8] focus:border-[#0d9488] focus:outline-none focus:ring-2 focus:ring-[#0d9488]/20"
                          />
                        </div>
                      ) : null}
                    </div>

                    <div className="space-y-3">
                      <button
                        type="button"
                        onClick={() => {
                          setRequirementType('best_of_breed');
                          if (validationErrors.requirementType) {
                            setValidationErrors((current) => ({ ...current, requirementType: undefined }));
                          }
                        }}
                        className={`w-full rounded-[14px] border p-4 text-left transition-colors ${
                          requirementType === 'best_of_breed'
                            ? 'border-[#0d9488] bg-[#f0fdfa]'
                            : 'border-[#e2e5eb] bg-white hover:border-[#0d9488]'
                        }`}
                      >
                        <p className="text-[14px] font-semibold text-[#0f172a]">
                          Use best-of-breed competencies for this role
                        </p>
                      </button>
                      <div className="rounded-[14px] border border-[#d1d5db] bg-[#f0fdfa] p-4 text-[13px] text-[#0f172a]">
                        Our team will design the simulation using industry-standard competencies for this role and level.
                        We&apos;ll share a draft for your review before activation.
                      </div>
                    </div>
                  </div>
                  {validationErrors.requirementType ? (
                    <p className="text-[12px] text-[#b91c1c]">{validationErrors.requirementType}</p>
                  ) : null}
                </div>

                <div className="space-y-1.5">
                  <label className="block text-[12px] font-semibold text-[#475569]">Additional Notes</label>
                  <textarea
                    value={notes}
                    onChange={(event) => setNotes(event.target.value)}
                    rows={3}
                    placeholder="Anything else we should know..."
                    className="w-full rounded-[10px] border border-[#e2e5eb] bg-white px-4 py-2.5 text-[14px] text-[#0f172a] placeholder:text-[#94a3b8] focus:border-[#0d9488] focus:outline-none focus:ring-2 focus:ring-[#0d9488]/20"
                  />
                </div>

                <div className="pt-1">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full rounded-[10px] bg-[#0d9488] px-5 py-2.5 text-[13px] font-semibold text-white transition-colors hover:bg-[#0f766e] sm:w-auto"
                    >
                      {submitting ? 'Submitting...' : 'Submit Request'}
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate('/employer/dashboard')}
                      className="w-full rounded-[10px] bg-[#0d9488] px-5 py-2.5 text-[13px] font-semibold text-white transition-colors hover:bg-[#0f766e] sm:w-auto"
                    >
                      Back
                    </button>
                  </div>
                  {submitError ? (
                    <p className="mt-3 text-[13px] text-[#dc2626]">{submitError}</p>
                  ) : null}
                </div>
              </form>
            </>
          ) : (
            <div className="space-y-4 text-center">
              <h1 className="font-display text-[24px] font-bold text-[#0f172a]">Request submitted</h1>
              <p className="text-[14px] text-[#64748b]">Our team will be in touch within 24 hours.</p>
              <button
                type="button"
                onClick={() => navigate('/employer/dashboard')}
                className="rounded-[10px] bg-[#0d9488] px-5 py-2.5 text-[13px] font-semibold text-white transition-colors hover:bg-[#0f766e]"
              >
                Back to Dashboard
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default RequestSimulation;
